/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { css, cx } from '@emotion/css';
import { Pagination } from '../Pagination';
import { SortIndicator } from './elements/SortIndicator';
import { TableBody } from './elements/TableBody';
import { EmptyTableRow } from './elements/EmptyTableRow';
import { SortKey, TableColumnProps } from './types';
import cvar from '../theme/cvar';
import { PublicComponentProps } from '../types';

export interface TableProps extends PublicComponentProps {
  data: any[];
  columns: TableColumnProps[];
  sortedBy?: SortKey;
  isCondensed?: boolean;
  hasHover?: boolean;
  isBordered?: boolean;
  noDataText?: string;
  onSortingRequested?: (soryKey: SortKey) => void;
  pageSize?: number;
  paginationPosition?: 'topRight' | 'bottomRight';
}

const commonCellBorders = `
  border-color: ${cvar('color-border-default')};
  border-style: solid;
  border-width: 0;
  border-top-width: 1px;
`;

const tableStyle = css`
  background-color: ${cvar('color-background')};
  border-collapse: collapse;
  border-spacing: 0;
  border: 1px solid ${cvar('color-border-default')};
  color: ${cvar('color-text-table-header')};
  margin-bottom: 18px;
  max-width: 100%;
  width: 100%;

  thead {
    background-color: ${cvar('color-table-header')};
    text-transform: uppercase;
    tr {
      th,
      td {
        ${commonCellBorders}
        border-bottom-width: 1px;
        font: ${cvar('text-table-header')};
        line-height: 12px;
        padding: ${cvar('spacing-16')} ${cvar('spacing-12')};
        text-align: left;
        vertical-align: top;
        :first-child {
          padding-left: ${cvar('spacing-32')};
        }
        :last-child {
          padding-right: ${cvar('spacing-32')};
        }
      }
    }
  }

  tbody {
    tr {
      td,
      th {
        ${commonCellBorders}
        padding: ${cvar('spacing-12')};
      }
    }
  }
`;

const condensedStyle = css`
  thead {
    tr {
      th,
      td {
        padding: ${cvar('spacing-8')};
      }
    }
  }
  tbody {
    font-size: 10px;
    line-height: 12px;
  }
`;

const hoverStyle = css`
  tbody {
    tr:hover {
      background-color: ${cvar('color-hover')};
    }
  }
`;

const borderedStyle = css`
  thead,
  tbody {
    tr {
      td,
      th {
        border-width: 1px;
      }
    }
  }
`;

export const Table = ({
  data,
  columns,
  sortedBy,
  noDataText,
  onSortingRequested,
  isCondensed,
  hasHover,
  isBordered,
  pageSize,
  paginationPosition,
  className,
  ...rest
}: TableProps) => {
  const [page, setPage] = useState<number>(0);

  const onHeaderSortClick = (c: TableColumnProps) => {
    if (!c.sortable) {
      return;
    }

    if (onSortingRequested) {
      onSortingRequested({
        key: c.accessor,
        desc: sortedBy && sortedBy.key === c.accessor ? !sortedBy.desc : sortedBy ? sortedBy.desc : false,
      });
    }
  };

  const total = (data || []).length;
  const hasPages = pageSize && total > pageSize;

  let pagination: JSX.Element | null = null;
  let tableData = data;

  if (hasPages && pageSize) {
    const start = page * pageSize;
    tableData = (data || []).slice(start, start + pageSize);
    pagination = hasPages ? (
      <Pagination
        initialPage={page}
        pageCount={Math.ceil(total / pageSize)}
        pageRangeDisplayed={2}
        marginPagesDisplayed={2}
        onPageChange={({ selected }: { selected: number }) => {
          setPage(selected);
        }}
      />
    ) : null;
  }

  return (
    <>
      {paginationPosition === 'topRight' ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{pagination}</div>
      ) : null}
      <table
        className={cx(
          'crc-table',
          tableStyle,
          isCondensed && condensedStyle,
          hasHover && hoverStyle,
          isBordered && borderedStyle,
          className,
        )}
        {...rest}
      >
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={`th-${i}-${c.accessor}`}>
                {c.sortable ? (
                  <div
                    tabIndex={0}
                    role="button"
                    style={{ textTransform: 'uppercase', cursor: c.sortable ? 'pointer' : undefined }}
                    onClick={() => onHeaderSortClick(c)}
                    onKeyDown={event => {
                      if (event.keyCode === 13 || event.keyCode === 32) {
                        event.preventDefault();
                        onHeaderSortClick(c);
                      }
                    }}
                  >
                    {c.Header}
                    {c.sortable ? <SortIndicator currentSort={sortedBy} accessor={c.accessor} /> : null}
                  </div>
                ) : (
                  c.Header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.length > 0 ? (
            <TableBody data={tableData} columns={columns} />
          ) : (
            <EmptyTableRow columns={columns} message={noDataText} />
          )}
        </tbody>
      </table>
      {paginationPosition === 'bottomRight' ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{pagination}</div>
      ) : null}
    </>
  );
};
